import { DeliveryPromise } from './DeliveryPromise';
import { InANutshellAvailability } from './enums/InANutshellEnums';
import { ProductExcluded } from './enums/ProductEnums';

export type BundleDealTypes = 'Main' | 'Child' | 'Alternative';
export type OurPlanetType = 'ourPlanet' | 'standard';

export type DTMComponentDataType = {
  componentInfo: {
    componentName: string;
    componentType: string;
  };
};

export type GAAddToBasketType = {
  ecommerce: {
    add: {
      products: Array<GAProductType>;
    };
    currencyCode: string;
    impressions: Array<Record<string, unknown>>;
    remove: Record<string, unknown>;
  };
  event: string;
};

export type GARemoveFromBasketType = {
  ecommerce: {
    add: Record<string, unknown>;
    currencyCode: string;
    impressions: Array<Record<string, unknown>>;
    remove: { products: Array<GAProductType> };
  };
  event: string;
};

export type GAUpdateBasketProductType = {
  ecommerce: {
    add?: { products: Array<GAProductType> };
    currencyCode: string;
    impressions: Array<Record<string, unknown>>;
    remove?: { products: Array<GAProductType> };
  };
  event: string;
};

export type GAProductEvent = 'productDetail' | 'productColourSwitch' | 'productSizePicked';

export type GAProductType = {
  brand: string | null;
  category: string | null;
  id: number | string;
  name: string;
  price?: number | string;
  quantity?: number;
  variant: string;
  colourId?: number;
  colorId?: number;
  colourCode?: string;
  productColorId?: number;
  colorCode?: string;
  discount?: number;
  sizeCode?: string;
  skuId?: string;
};

export type GAPurchaseStepActionField = {
  affiliation: string;
  currencyCode: string;
  id: string;
  netGoodsValue: number;
  promoCode: string;
  promoDisplayCode: string;
  promoType: string;
  revenue: number;
  revenueWithoutDelivery: number;
  shipping: number;
  step: number;
  tax: number;
};

export type GAPurchaseStep = {
  purchase: {
    actionField: GAPurchaseStepActionField;
    products: Array<GAProductType>;
  };
};

export type DTMBasicDigitalDataType = {
  cart: {
    cartID: string;
  };
  event: Array<DTMEventType>;
  links: Array<Record<string, unknown>>;
  page: Partial<{
    attributes: Partial<{
      brand: string;
      market: string;
      errorMessageForms: string;
      primaryCategory: string;
      addCardLocation: string;
    }>;
    pageInfo: Partial<{
      pageID: string;
      pageType: string;
    }>;
  }>;
  product: Array<DTMProductType>;
  user: Partial<{
    cart: {
      cartID: string;
    };
    attributes: {
      newsletterOptin: boolean;
    };
  }>;
  transaction: Partial<{
    promoCode: string;
    promoType: string;
    total: Partial<{
      payments: Array<{ paymentMethod: string; paymentGroup: string }>;
      shippingMethod: string;
      voucherDiscount: string;
      currencyCode: string;
    }>;
    currencyCode: string;
    paymentMethod: string;
    shipping: number;
    shippingMethod: string;
    voucherDiscount: string;
    transactionID: string;
  }>;
  component: DTMComponentDataType;
};

export type DTMAddToBasketType = Partial<DTMBasicDigitalDataType>;
export type DTMRemoveFromBasketType = Partial<DTMBasicDigitalDataType>;
export type DTMUpdateBasketProductType = Partial<DTMBasicDigitalDataType>;
export type DTMBasketOverviewType = Partial<DTMBasicDigitalDataType>;
export type DTMCheckoutType = Partial<DTMBasicDigitalDataType>;

export type DTMEventType = {
  eventInfo: {
    directCallRule: boolean;
    eventAction: string;
    ruleName: string;
    eventName?: string;
  };
};

export type DTMProductType = {
  attributes: {
    brand?: string;
    brandValue?: string;
    buyingGroup?: string;
    gender?: string;
    productQualifier?: string;
    sellPrice?: number;
    RRPSTD?: number;
    productPriceOverlay?: string;
    productRating?: number;
    productReviews?: number;
    productDiscountPercentage?: number;
    productImages?: number;
    lastPieces?: boolean;
    productTemplate?: string;
    sizeTable?: AnalyticsAvailabilityTypes;
    productSizeTable?: AnalyticsSizeGuideViewedTypes;
    excludeHomeDelivery?: ProductExcluded.HD_EXCLUDED | ProductExcluded.HD_NOT_EXCLUDED;
    excludeClickAndCollect?: ProductExcluded.CC_EXCLUDED | ProductExcluded.CC_NOT_EXCLUDED;
    excludeStoreSourcing?: ProductExcluded.SS_EXCLUDED | ProductExcluded.SS_NOT_EXCLUDED;
    bundleProductType?: BundleDealTypes;
    ourPlanet?: OurPlanetType;
    inANutshellFeatures?: InANutshellAvailability;
    productTilePosition?: number;
  };
  productInfo: {
    color?: string;
    discount?: string;
    price?: string;
    productID?: string;
    productName?: string;
    quantity?: string;
    size?: string;
    colorId?: number;
    colorCode?: string;
    expertReview?: boolean;
  };
  deliveryPromiseInfo?: Partial<DeliveryPromise>;
};

export enum AnalyticsAvailabilityTypes {
  AVAILABLE = 'available',
  NOT_AVAILABLE = 'not available',
}

export enum AnalyticsSizeGuideViewedTypes {
  VIEWED = 'viewed',
  NOT_VIEWED = 'not viewed',
}

export type CompareProductsFeaturesAnalyticsData = {
  id: number;
  featuresAmount: number;
};

export type ProductSearchPersonalizationAnalyticsData = {
  personalized: boolean;
  traits: Array<string>;
};

export type PaymentRequestAnalyticsData = {
  paymentGroup: string;
  paymentMethod?: string;
};
